import {useEffect, useState, useCallback} from 'react';
import {TTSService} from './TTSService';
import {Platform} from 'react-native';

export function useTTSState() {
  const service = TTSService.getInstance();

  // Initialize local React state from service
  const [isReading, setIsReading] = useState(service.getIsReading());
  const [isVoiceLoading, setIsVoiceLoading] = useState(
    service.getIsVoiceLoading(),
  );
  const [isVoiceError, setIsVoiceError] = useState(service.getIsVoiceError());

  // Initialize TTS service on first mount
  useEffect(() => {
    service.init();
  }, [service]);

  // For web: handle voice list loading which happens asynchronously
  useEffect(() => {
    if (Platform.OS === 'web' && window.speechSynthesis) {
      // SpeechSynthesis.getVoices() may not return voices immediately
      // in some browsers, so we need to listen for the voiceschanged event
      const handleVoicesChanged = () => {
        // Just re-initialize the service when voices change/become available
        service.init();
      };

      // Safari sometimes needs this to load voices initially
      window.speechSynthesis.onvoiceschanged = handleVoicesChanged;

      // Force a check for voices right away
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        service.init();
      }

      return () => {
        window.speechSynthesis.onvoiceschanged = null;
      };
    }
  }, [service]);

  // Subscribe to changes from TTSService
  useEffect(() => {
    const unsubscribe = service.subscribe(newState => {
      setIsReading(newState.isReading);
      setIsVoiceLoading(newState.isVoiceLoading);
      setIsVoiceError(newState.isVoiceError);
    });

    // Cleanup on unmount
    return () => {
      unsubscribe();
    };
  }, [service]);

  // Wrap startReading & stopReading in stable callbacks
  const startReading = useCallback(
    async (text: string, customLanguage?: string) => {
      await service.startReading(text, customLanguage);
    },
    [service],
  );

  const stopReading = useCallback(async () => {
    await service.stopReading();
  }, [service]);

  return {
    isReading,
    isVoiceLoading,
    isVoiceError,
    startReading,
    stopReading,
  };
}
